// Client/src/utils/stateLrs/massdot.js
// MassDOT (Massachusetts DOT) LRS adapter. Data-source facts verified live
// 2026-07-26 — see memory/reference_massdot_lrs_road_inventory_api.md.
//
// Source: the Road Inventory year-end FeatureServer. Layer 11 is the 2025
// inventory; layers 0-10 are 2014-2024. The YEAR IS THE LAYER, so unlike ALDOT
// there is no temporal (`ToDate IS NULL`) filter to apply — pointing at layer 11
// already means "current".
//
// One layer carries the whole network (504,907 segments); `Route_System`
// classifies it. I/US/SR are the numbered routes that carry posted mileposts;
// L/N/M/R/B are local roads, ramps and paths that have measures but no signs.
// We map that split onto the same milepost/milepoint networks the ALDOT adapter
// uses, so shared consumers (formatLrsLocation, lane-points enrichment) keep
// working unchanged.

import {
  measureRangesFromSections,
  interpolatePointAtMeasure,
  projectPointToMeasure,
  smoothRouteRuns,
} from './lrsMath';
import { MA_COUNTY_NAMES, MA_CITY_NAMES, maDistrictLabel } from './maPlaces';

// Route_System coded-value domain (dRouteSystem_10), verbatim from the layer.
export const ROUTE_SYSTEM_LABEL = {
  I: 'I', US: 'US', SR: 'SR', R: 'Ramp',
  L: 'Local', M: 'Misc', N: 'Road', B: 'Bike/Ped',
};

// Only these carry field-posted mileposts. Everything else has a measure but
// nothing signed on the roadside.
const NUMBERED_SYSTEMS = new Set(['I', 'US', 'SR']);

// ─── The two speed records ────────────────────────────────────────────────────
// MassDOT carries TWO speed columns, and they are not two speed limits — a road
// has one. They are two independently-maintained RECORDS of that one limit, and
// we surface both rather than silently picking a winner:
//
//  - `Speed_Lim` ("Speed Limit Posted") — a field inventory of the physical
//    signs. 91,962 segments. Noisy in the way observation is: it contains 12,
//    27, 28 and 31 mph, all on private ways where a town really did post that.
//  - `Speed` ("Speed Limit Regulation") — the limit set by a Special Speed
//    Regulation on file with MassDOT. 81,218 segments. Clean 5-mph steps only,
//    because legal instruments are written that way.
//
// They disagree on 35.5 % of the 50,127 segments carrying both, almost always
// about WHERE a limit changes rather than what it is: on I-195 EB the regulation
// drops 65→55 at MP 9.43 while the sign drops at MP 10.02, so the 0.59 mi
// between them reads 65/55. Disagreement bands there run a median 0.71 mi
// against a 4.04 mi median for agreement, and 85 % of statewide gaps are within
// two 5-mph steps.
//
// Because of that, the two are kept in SEPARATE keys and never blended. A
// coalescing fallback would render one real speed change as two steps ~0.6 mi
// apart, and leave a consumer unable to tell which register a number came from.
// `Speed IS NULL` also does not mean "no limit" — Massachusetts statutory
// defaults (MGL c.90 §17) apply with no sign and no filed regulation.

// Sentinel for "not posted / unknown". A real limit is never 999.
const SPEED_LIMIT_UNKNOWN = 999;

/** Shared cleaner for BOTH speed fields (see the two-records note above).
 *
 *  MassDOT reports "unknown" several ways and they must all read as null:
 *   - a plain JSON null — the commonest by far, 361,424 of 504,907 segments on
 *     `Speed_Lim`. This has to be rejected BEFORE Number(), because
 *     `Number(null)` is 0, which is finite and isn't 999, and would otherwise
 *     claim the road is posted at zero mph.
 *   - the 999 sentinel — 51,521 segments on `Speed_Lim`. Never appears on
 *     `Speed`, but costs nothing to guard on both.
 *   - 0 — 2 segments on `Speed`, none on `Speed_Lim`. Not a road at a
 *     standstill; another way of writing "no value". */
function normalizeSpeedLimit(raw) {
  if (raw == null) return null;
  const speed = Number(raw);
  if (!Number.isFinite(speed)) return null;
  if (speed <= 0 || speed === SPEED_LIMIT_UNKNOWN) return null;
  return speed;
}

/** Directions whose measures run OPPOSITE to the field-posted mileposts.
 *  Verified against the Exits layer (MA renumbered exits to milepost-based in
 *  2021): on NB/EB the LRS measure IS the posted milepost (mean deviation
 *  0.32-0.44 mi over 24-71 exits); on SB/WB the posted value is
 *  `routeLength - measure` (mean deviation 0.38-0.50 mi). */
const REVERSED_DIRECTIONS = new Set(['SB', 'WB']);

export function isReversedDirection(routeDirection) {
  return REVERSED_DIRECTIONS.has(String(routeDirection ?? '').toUpperCase());
}

export function isNumberedSystem(routeSystem) {
  return NUMBERED_SYSTEMS.has(String(routeSystem ?? '').toUpperCase());
}

// ─── Posted-milepost conversion ───────────────────────────────────────────────

/** LRS measure → field-posted milepost. Returns null when the route length is
 *  unknown or the route carries no posted mileposts, so callers fall back to
 *  showing the raw measure rather than inventing a number. */
export function toPostedMilepost({ measure, routeDirection, routeLength, routeSystem }) {
  if (!Number.isFinite(measure)) return null;
  if (routeSystem != null && !isNumberedSystem(routeSystem)) return null;
  if (!isReversedDirection(routeDirection)) return measure;
  return Number.isFinite(routeLength) ? routeLength - measure : null;
}

/** Field-posted milepost → LRS measure (the inverse; the picker takes posted
 *  input and must query the service in measure space). */
export function fromPostedMilepost({ posted, routeDirection, routeLength }) {
  if (!Number.isFinite(posted)) return null;
  if (!isReversedDirection(routeDirection)) return posted;
  return Number.isFinite(routeLength) ? routeLength - posted : null;
}

// ─── Route keys ───────────────────────────────────────────────────────────────
// `route_id` values contain spaces ("I93 SB") and hyphens ("I190-DUP NB"), so
// the key is split on the FIRST colon only — never on '-' the way the ALDOT
// key parser does.

export function routeKeyFor(routeId, routeSystem) {
  const network = isNumberedSystem(routeSystem) ? 'milepost' : 'milepoint';
  return `${network}:${routeId}`;
}

export function parseRouteKey(key) {
  const s = String(key ?? '');
  const i = s.indexOf(':');
  if (i < 0) return { network: 'milepost', routeId: s };
  return { network: s.slice(0, i), routeId: s.slice(i + 1) };
}

// ─── Zones ────────────────────────────────────────────────────────────────────

/** Massachusetts has no measure-reset zone (measures run the length of each
 *  route), so the zone is purely descriptive: municipality + county, with the
 *  MassDOT highway district as the region analogue. */
export function buildZone({ countyCode, cityCode, hwyDist }) {
  const county = MA_COUNTY_NAMES[String(countyCode)] ?? null;
  const city = MA_CITY_NAMES[String(cityCode)] ?? null;
  if (!county && !city) return null;
  const district = maDistrictLabel(hwyDist);
  return {
    kind: 'municipality',
    code: cityCode != null ? String(cityCode) : null,
    name: city ?? county,
    county,
    region: district,
    // Consumed verbatim by formatLrsLocation so we get "· MassDOT District 6"
    // instead of the ALDOT-shaped "· East Central Region".
    regionLabel: district,
  };
}

// ─── Normalization ────────────────────────────────────────────────────────────

/** ArcGIS query JSON → NormalizedSection[]. ~0.6 % of features come back with
 *  null geometry; they normalize to an empty `paths` and are skipped by the
 *  math rather than dropped here, matching the ALDOT adapter's behaviour. */
export function normalizeMassdotSections(arcgisJson) {
  return (arcgisJson?.features ?? []).map((f) => {
    const a = f.attributes ?? {};
    return {
      routeId: a.route_id ?? null,
      routeSystem: a.Route_System ?? null,
      routeNumber: a.Route_Number ?? null,
      routeDirection: a.Route_Direction ?? null,
      streetName: a.St_Name ?? null,
      cityCode: a.City ?? null,
      countyCode: a.County ?? null,
      hwyDist: a.Hwy_Dist ?? null,
      speedLimit: normalizeSpeedLimit(a.Speed_Lim),
      speedRegulatory: normalizeSpeedLimit(a.Speed),
      fromMeasure: Number.isFinite(Number(a.from_measure)) ? Number(a.from_measure) : null,
      toMeasure: Number.isFinite(Number(a.to_measure)) ? Number(a.to_measure) : null,
      paths: f.geometry?.paths ?? [],
    };
  });
}

// ─── Labels ───────────────────────────────────────────────────────────────────

/** "I-93 SB", "SR-2 EB", or the street name for unnumbered roads. */
export function routeLabelFor({ routeId, routeSystem, routeNumber, routeDirection, streetName, cityCode }) {
  const dir = routeDirection ? ` ${routeDirection}` : '';
  if (isNumberedSystem(routeSystem)) {
    const sys = ROUTE_SYSTEM_LABEL[routeSystem] ?? routeSystem;
    return `${sys}-${routeNumber}${dir}`;
  }
  const city = MA_CITY_NAMES[String(cityCode)];
  const name = streetName ? titleCase(streetName) : (routeId ?? 'Unnamed road');
  return city ? `${name} (${city})${dir}` : `${name}${dir}`;
}

/** MassDOT stores street names SHOUTING ("JOHN F FITZGERALD EXPRESSWAY"). */
export function titleCase(s) {
  return String(s ?? '')
    .toLowerCase()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

// ─── Fetch layer / adapter ────────────────────────────────────────────────────

const FEATURESERVER =
  'https://gis.massdot.state.ma.us/arcgis/rest/services/Roads/RoadInventoryYearEndFiles/FeatureServer';

// ─── Special Speed Regulation (SSR) snapshot ──────────────────────────────────
// Customer-delivered authoritative speed source (2026-08-25): MassDOT's adopted speed
// regulations as a measure-keyed snapshot, built per district delivery by
// infra/lrs_snapshot/build_massdot_ssr.py. PRIMARY source for regulatory speed on
// MassDOT projects; the Road Inventory `Speed` attribute below remains the intact
// FALLBACK — any snapshot failure, or a measure no regulation covers, behaves exactly
// as before the SSR data existed (that fallback IS the "keep the current interface as
// backup" requirement).
//
// Rows are matched by MEASURE INTERVAL on the same LRS axis `locationFromHit` already
// resolves — no spatial query. Tier precedence (operator decision, "never a gap"):
//   new (adopted <3y, ~10 ft) > legacy (~200 ft) > legacy_mismatch (flagged
//   'Distance Mismatch' by MassDOT) > road_inventory (live service value).
const SSR_SNAPSHOT_BASE = 'https://rsa-lrs-snapshots.s3.amazonaws.com/lrs/MA/ssr/v1';
const SSR_TIER_RANK = { new: 0, legacy: 1, legacy_mismatch: 2 };

/** Pick the regulation governing `measure` from a route's SSR rows: highest tier,
 *  then narrowest interval (most specific regulation), then latest date. Null when
 *  no row covers the measure — the caller falls back to the Road Inventory value.
 *  Exported for testing. */
export function resolveSsrSpeed(rows, measure) {
  const m = Number(measure);
  if (!Array.isArray(rows) || !Number.isFinite(m)) return null;
  let best = null;
  for (const r of rows) {
    if (!r || !Number.isFinite(r.speed)) continue;
    if (!(r.from <= m && m <= r.to)) continue;
    const rank = SSR_TIER_RANK[r.tier];
    if (rank === undefined) continue;
    if (
      best === null ||
      rank < best.rank ||
      (rank === best.rank && (r.to - r.from) < best.width) ||
      (rank === best.rank && (r.to - r.from) === best.width &&
        String(r.date ?? '') > String(best.row.date ?? ''))
    ) {
      best = { row: r, rank, width: r.to - r.from };
    }
  }
  return best ? best.row : null;
}
// Layer 11 = Road Inventory 2025. Bump this (and RE-VERIFY the field list) when
// MassDOT publishes a new year-end layer; older years stay addressable at 0-10.
const LAYER_INVENTORY = 11;
export const INVENTORY_YEAR = 2025;

const FETCH_TIMEOUT_MS = 15000;
// One transient burst of HTTP-200-with-error-body failures was observed on
// 2026-07-26 (four consecutive queries failed, then 30/30 succeeded unchanged).
// The ALDOT adapter has no fetch-level retry and surfaces such blips straight to
// the user; here a single cheap retry absorbs them.
const FETCH_RETRIES = 1;
const RETRY_DELAY_MS = 400;

const LOCAL_SEARCH_LIMIT = 50;
// Attribute rows scanned for a local search before client-side dedupe. Local
// roads are segmented per attribute change, so one street is many rows.
const LOCAL_SEARCH_SCAN = 400;
const LOCATE_RADIUS_METERS = 150;

const GEOMETRY_FIELDS =
  'route_id,from_measure,to_measure,Route_System,Route_Number,Route_Direction,St_Name,City,County,Hwy_Dist,Speed_Lim,Speed';

export class LrsUnavailableError extends Error {
  constructor(message, detail = null) {
    super(message);
    this.name = 'LrsUnavailableError';
    this.detail = detail;
  }
}

function buildQueryUrl(layerId, params) {
  const qs = new URLSearchParams({ f: 'json', ...params });
  return `${FEATURESERVER}/${layerId}/query?${qs.toString()}`;
}

/** Escape a single-quoted SQL literal for an ArcGIS WHERE clause. */
function sqlLiteral(value) {
  return String(value ?? '').replace(/'/g, "''");
}

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

export function createMassdotAdapter({ fetchImpl } = {}) {
  const doFetch = fetchImpl ?? ((...args) => fetch(...args));
  const geometryCache = new Map();  // routeKey → { sections, mRanges, routeLength }
  const ssrCache = new Map();       // routeId ("SR2 EB") → rows[] | null (null = unavailable)
  let statewideCache = null;        // RouteEntry[]

  /** SSR rows for one LRS route, snapshot-fetched once and cached (null on ANY
   *  failure — 404 for a route/district not yet delivered, bucket absent, network —
   *  so the caller silently keeps the Road Inventory speeds, exactly as before the
   *  SSR data existed). File key = Route_ID with spaces as hyphens ("SR2-EB.json"). */
  async function loadSsrRoute(routeId) {
    const id = String(routeId ?? '').trim();
    if (!id) return null;
    if (ssrCache.has(id)) return ssrCache.get(id);
    let rows = null;
    try {
      // fetchJsonOnce, not fetchJson: a snapshot 404 (route/district not delivered yet,
      // or the bucket absent) is definitive — retrying it would only stall enrichment.
      const snap = await fetchJsonOnce(
        `${SSR_SNAPSHOT_BASE}/routes/${encodeURIComponent(id.replace(/ /g, '-'))}.json`,
      );
      if (Array.isArray(snap?.rows) && snap.rows.length > 0) rows = snap.rows;
    } catch (_) { /* fall back to Road Inventory speeds */ }
    ssrCache.set(id, rows);
    return rows;
  }
  let statewidePromise = null;      // in-flight guard so parallel callers share one fetch
  const routeLengths = new Map();   // route_id → total miles (numbered routes)
  const locateCache = new Map();    // "lat,lng"@5dp → Promise<LrsPointLocation|null>
  let sourceInfo = { statewideSource: null, snapshotDate: null };

  async function fetchJsonOnce(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await doFetch(url, { signal: controller.signal });
      if (!res.ok) {
        throw new LrsUnavailableError(
          'The road data service is unavailable right now.',
          `HTTP ${res.status} from ${url}`,
        );
      }
      const body = await res.json();
      // ArcGIS reports failures as HTTP 200 with an error body.
      if (body && body.error) {
        throw new LrsUnavailableError(
          'The road data service returned an error.',
          `ArcGIS error ${body.error.code}: ${body.error.message}`,
        );
      }
      return body;
    } catch (err) {
      if (err instanceof LrsUnavailableError) throw err;
      throw new LrsUnavailableError(
        'Could not reach the road data service (network problem or timeout).',
        err?.message ?? null,
      );
    } finally {
      clearTimeout(timer);
    }
  }

  async function fetchJson(url) {
    let lastErr = null;
    for (let attempt = 0; attempt <= FETCH_RETRIES; attempt += 1) {
      try {
        return await fetchJsonOnce(url);
      } catch (err) {
        lastErr = err;
        if (attempt < FETCH_RETRIES) await delay(RETRY_DELAY_MS);
      }
    }
    throw lastErr;
  }

  /** Layer query with resultOffset pagination. maxRecordCount is 2,000,000 and
   *  the statewide numbered network (32k features) comes back in one page — the
   *  loop is a safety net for a server-side cap change. */
  async function fetchAllFeatures(params) {
    const features = [];
    let offset = 0;
    for (;;) {
      const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
        ...params, resultOffset: String(offset),
      }));
      features.push(...(body.features ?? []));
      if (!body.exceededTransferLimit || !body.features?.length) break;
      offset += body.features.length;
    }
    return { features };
  }

  /** The route picker's whole payload: one server-side groupBy returning 314
   *  numbered routes with their measure extents (~45 KB). No geometry is pulled
   *  until a route is actually selected, which is why this adapter needs no
   *  S3 snapshot the way the ALDOT one does. */
  async function fetchRouteIndex() {
    const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
      where: "Route_System IN ('I','US','SR')",
      groupByFieldsForStatistics: 'route_id,Route_System,Route_Number,Route_Direction',
      outStatistics: JSON.stringify([
        { statisticType: 'min', onStatisticField: 'from_measure', outStatisticFieldName: 'mp_lo' },
        { statisticType: 'max', onStatisticField: 'to_measure', outStatisticFieldName: 'mp_hi' },
      ]),
      orderByFields: 'route_id',
    }));
    return (body.features ?? []).map((f) => {
      const a = f.attributes ?? {};
      const lo = Number(a.mp_lo);
      const hi = Number(a.mp_hi);
      const routeLength = Number.isFinite(hi) ? hi : null;
      return {
        key: routeKeyFor(a.route_id, a.Route_System),
        network: 'milepost',
        routeId: a.route_id,
        label: routeLabelFor({
          routeId: a.route_id,
          routeSystem: a.Route_System,
          routeNumber: a.Route_Number,
          routeDirection: a.Route_Direction,
        }),
        routeType: a.Route_System,
        routeNumber: a.Route_Number,
        routeDirection: a.Route_Direction,
        countyCode: null,
        routeLength,
        reversed: isReversedDirection(a.Route_Direction),
        mRanges: Number.isFinite(lo) && Number.isFinite(hi) ? [[lo, hi]] : [],
      };
    });
  }

  /** Populate + cache the numbered-route index. Shared by listStatewideRoutes
   *  and by posted-milepost conversion (which needs each route's total length),
   *  so a map click can label a milepost without the Routes panel ever opening. */
  function ensureRouteIndex() {
    if (statewideCache) return Promise.resolve(statewideCache);
    if (!statewidePromise) {
      statewidePromise = fetchRouteIndex()
        .then((entries) => {
          for (const e of entries) {
            if (Number.isFinite(e.routeLength)) routeLengths.set(e.routeId, e.routeLength);
          }
          statewideCache = entries;
          sourceInfo = { statewideSource: 'live', snapshotDate: null };
          return entries;
        })
        .finally(() => { statewidePromise = null; });
    }
    return statewidePromise;
  }

  /** Route length in miles, or null if not a known numbered route. Never
   *  throws — posted-milepost display degrades to the raw measure. */
  async function routeLengthFor(routeId) {
    if (routeLengths.has(routeId)) return routeLengths.get(routeId);
    try {
      await ensureRouteIndex();
    } catch (_) { /* leave unknown */ }
    return routeLengths.has(routeId) ? routeLengths.get(routeId) : null;
  }

  async function fetchRouteSections(routeId) {
    const raw = await fetchAllFeatures({
      where: `route_id = '${sqlLiteral(routeId)}'`,
      outFields: GEOMETRY_FIELDS,
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
    });
    return normalizeMassdotSections(raw);
  }

  async function getRouteGeometry(key) {
    if (geometryCache.has(key)) return geometryCache.get(key);
    const { routeId } = parseRouteKey(key);
    const sections = await fetchRouteSections(routeId);
    if (!sections.length) {
      throw new LrsUnavailableError(`No geometry found for route ${routeId}.`);
    }
    const mRanges = measureRangesFromSections(sections);
    const declared = sections
      .map((s) => s.toMeasure)
      .filter((m) => Number.isFinite(m));
    const routeLength = declared.length ? Math.max(...declared) : null;
    if (Number.isFinite(routeLength) && isNumberedSystem(sections[0]?.routeSystem)) {
      routeLengths.set(routeId, routeLength);
    }
    const result = { sections, mRanges, routeLength };
    geometryCache.set(key, result);
    return result;
  }

  async function querySectionsNear(point) {
    const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
      geometry: JSON.stringify({ x: point.lng, y: point.lat, spatialReference: { wkid: 4326 } }),
      geometryType: 'esriGeometryPoint',
      inSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      distance: String(LOCATE_RADIUS_METERS),
      units: 'esriSRUnit_Meter',
      outFields: GEOMETRY_FIELDS,
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
    }));
    return normalizeMassdotSections(body);
  }

  /** Nearest section to `point`, preferring numbered routes when they are
   *  within `numberedBiasMeters` of the closest hit. Dense interchanges return
   *  20+ ramps and service roads within 150 m (21 at the Zakim Bridge), so a
   *  pure nearest-wins rule labels a highway click with a ramp. */
  function nearestOnSections(sections, point, { numberedBias = 25 } = {}) {
    const hits = [];
    for (const s of sections) {
      const proj = projectPointToMeasure([s], point);
      if (!proj || proj.distanceMeters > LOCATE_RADIUS_METERS) continue;
      hits.push({ section: s, m: proj.m, distanceMeters: proj.distanceMeters });
    }
    if (!hits.length) return null;
    hits.sort((a, b) => a.distanceMeters - b.distanceMeters);
    const closest = hits[0];
    const preferred = hits.find(
      (h) => isNumberedSystem(h.section.routeSystem)
        && h.distanceMeters <= closest.distanceMeters + numberedBias,
    );
    return preferred ?? closest;
  }

  /** Same preference rule `nearestOnSections` applies between sections, lifted
   *  to the choice BETWEEN routes: nearest wins unless a numbered route sits
   *  within `numberedBias` of it. */
  function pickCandidate(perRoute, { numberedBias = 25 } = {}) {
    if (!perRoute.length) return null;
    const sorted = [...perRoute].sort((a, b) => a.hit.distanceMeters - b.hit.distanceMeters);
    const closest = sorted[0];
    const preferred = sorted.find(
      (c) => isNumberedSystem(c.hit.section.routeSystem)
        && c.hit.distanceMeters <= closest.hit.distanceMeters + numberedBias,
    );
    return preferred ?? closest;
  }

  function locationFromHit(hit, routeLength) {
    const s = hit.section;
    const measure = hit.m;
    const posted = toPostedMilepost({
      measure,
      routeDirection: s.routeDirection,
      routeLength,
      routeSystem: s.routeSystem,
    });
    return {
      routeKey: routeKeyFor(s.routeId, s.routeSystem),
      routeLabel: routeLabelFor(s),
      network: isNumberedSystem(s.routeSystem) ? 'milepost' : 'milepoint',
      measure,
      postedMilepost: posted,
      zone: buildZone(s),
      distanceMeters: hit.distanceMeters,
      speedLimit: s.speedLimit,
      speedRegulatory: s.speedRegulatory,
      streetName: s.streetName ? titleCase(s.streetName) : null,
    };
  }

  return {
    stateCode: 'MA',
    stateName: 'Massachusetts (MassDOT)',
    capabilities: {
      // Speed_Lim is populated on 91,962 of 504,907 segments (~18 %).
      speedLimits: true,
      // [2026-08-25] Build lane speed zones from the SPECIAL SPEED REGULATION
      // (`Speed` -> speed_limit_regulatory), not the posted sign inventory:
      // the regulation is the legally enforceable limit MassDOT audit against,
      // and their posted inventory covers only ~18 % of segments. Posted stays
      // the fallback per point (see laneSpeeds.speedForLrsPoint). MassDOT ONLY
      // — every other adapter omits this key and keeps posted-first.
      // On SR-2 (project 2468) the two disagree on 14.3 % of lane points, by as
      // much as 20 mph (25->45, 40->50, 50->45), so this materially moves
      // tested speed and every sight-distance number derived from it.
      speedFieldPreference: 'regulatory',
      // [2026-08-25] Regulatory speeds come from the customer-delivered SSR
      // snapshot when it covers a measure (see SSR_SNAPSHOT_BASE), with the
      // Road Inventory as the automatic per-point fallback. Informational —
      // the mechanism is snapshot-first-with-fallback, not a switch.
      speedSource: 'ssr',
      // Measures on SB/WB routes run opposite to the posted mileposts, so the
      // UI must convert rather than print the raw value.
      postedMilepost: true,
      localRoadSearch: true,
    },
    inventoryYear: INVENTORY_YEAR,

    getSourceInfo() {
      return { ...sourceInfo };
    },

    async listStatewideRoutes() {
      return ensureRouteIndex();
    },

    /** Local-road search. MassDOT carries `St_Name`, which ALDOT does not, so
     *  this is a real name search rather than a coded-designation match. */
    async searchLocalRoutes(term) {
      const cleaned = String(term ?? '').trim();
      if (!cleaned) return [];
      const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
        where: buildLocalWhere(cleaned),
        outFields: 'route_id,St_Name,Route_System,Route_Number,Route_Direction,City,County,Hwy_Dist',
        returnGeometry: 'false',
        orderByFields: 'route_id',
        resultRecordCount: String(LOCAL_SEARCH_SCAN),
      }));
      const seen = new Set();
      const entries = [];
      for (const s of normalizeMassdotSections(body)) {
        const key = routeKeyFor(s.routeId, s.routeSystem);
        if (seen.has(key)) continue;
        seen.add(key);
        entries.push({
          key,
          network: isNumberedSystem(s.routeSystem) ? 'milepost' : 'milepoint',
          routeId: s.routeId,
          label: routeLabelFor(s),
          routeType: s.routeSystem,
          routeNumber: s.routeNumber,
          routeDirection: s.routeDirection,
          countyCode: s.countyCode != null ? String(s.countyCode) : null,
          zone: buildZone(s),
          reversed: isReversedDirection(s.routeDirection),
          routeLength: null, // unknown until geometry is fetched
          mRanges: null,
        });
        if (entries.length >= LOCAL_SEARCH_LIMIT) break;
      }
      return entries;
    },

    getRouteGeometry,

    /** Posted milepost → lat/lng. Input is in POSTED space (what the user
     *  reads off a sign); it is converted to LRS measure before interpolation. */
    async measureToPoint(key, posted) {
      const { sections, routeLength } = await getRouteGeometry(key);
      const direction = sections[0]?.routeDirection;
      const measure = fromPostedMilepost({ posted, routeDirection: direction, routeLength });
      if (!Number.isFinite(measure)) return null;
      return interpolatePointAtMeasure(sections, measure);
    },

    /** lat/lng → posted milepost (falls back to the raw measure on routes with
     *  no posted mileposts, so local roads still return a usable number). */
    async pointToMeasure(key, point) {
      const { sections, routeLength } = await getRouteGeometry(key);
      const r = projectPointToMeasure(sections, point);
      if (!r) return null;
      const posted = toPostedMilepost({
        measure: r.m,
        routeDirection: sections[0]?.routeDirection,
        routeLength,
        routeSystem: sections[0]?.routeSystem,
      });
      return posted ?? r.m;
    },

    /** Raw LRS measure for a point — the value MassDOT's own data uses. */
    async pointToRawMeasure(key, point) {
      const { sections } = await getRouteGeometry(key);
      const r = projectPointToMeasure(sections, point);
      return r ? r.m : null;
    },

    toPostedMilepost,
    fromPostedMilepost,

    /** Nearest road + measure within 150 m of a clicked point. Fire-and-forget
     *  safe: resolves null on any failure, caches (incl. in-flight) per ~1 m. */
    locatePoint(point) {
      if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
        return Promise.resolve(null);
      }
      const cacheKey = `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`;
      if (locateCache.has(cacheKey)) return locateCache.get(cacheKey);
      const promise = (async () => {
        const sections = await querySectionsNear(point);
        const hit = nearestOnSections(sections, point);
        if (!hit) return null;
        const routeLength = isNumberedSystem(hit.section.routeSystem)
          ? await routeLengthFor(hit.section.routeId)
          : null;
        return locationFromHit(hit, routeLength);
      })().catch(() => null);
      locateCache.set(cacheKey, promise);
      return promise;
    },

    /** Per-point LRS enrichment for a lane's points — same contract as the
     *  ALDOT adapter: an array aligned 1:1 with `points`, each entry
     *  { road, milepost, county, region, distance_m } or null. `milepost` is the
     *  POSTED value; `lrs_measure` carries the raw MassDOT measure alongside it
     *  so downstream reports can cross-reference MassDOT's own data.
     *
     *  MassDOT adds `speed_limit` (posted) and `speed_limit_regulatory` — two
     *  records of the one limit, kept separate and never coalesced; see the
     *  two-records note at the top of this file. Both are always present, and
     *  `null` means MassDOT has no value on record. That is distinct from the
     *  key being ABSENT, which is what an adapter with no speed data at all
     *  (ALDOT, `capabilities.speedLimits: false`) produces. */
    async enrichPointsWithLrs(routeKeys, points) {
      let keys = [...new Set((routeKeys || []).filter(Boolean))];
      const pts = Array.isArray(points) ? points : [];
      if (!pts.length) return [];

      if (!keys.length) {
        // No binding (manually drawn route): discover the nearest routes from a
        // few anchors instead of one spatial query per point.
        try {
          const anchorIdxs = [...new Set([0, Math.floor(pts.length / 2), pts.length - 1])];
          const found = new Set();
          for (const idx of anchorIdxs) {
            const pt = pts[idx];
            if (!pt) continue;
            const sections = await querySectionsNear(pt).catch(() => []);
            const hit = nearestOnSections(sections, pt);
            if (hit) found.add(routeKeyFor(hit.section.routeId, hit.section.routeSystem));
          }
          keys = [...found];
        } catch (_) {
          keys = [];
        }
      }
      if (!keys.length) return pts.map(() => null);

      const sectionSets = [];
      for (const key of keys) {
        try {
          const { sections, routeLength } = await getRouteGeometry(key);
          if (Array.isArray(sections) && sections.length) {
            sectionSets.push({ key, sections, routeLength });
          }
        } catch (_) { /* skip a key whose geometry can't be loaded */ }
      }
      if (!sectionSets.length) return pts.map(() => null);

      // SSR regulations per candidate route, prefetched here because the per-point
      // mapping below is synchronous. A route with no snapshot maps to null and its
      // points keep the Road Inventory speeds (the backup path).
      const ssrByKey = new Map();
      for (const set of sectionSets) {
        ssrByKey.set(set.key, await loadSsrRoute(parseRouteKey(set.key).routeId));
      }

      // Best hit PER ROUTE per point (not just the single nearest), so a point
      // can be re-assigned to a neighbouring route by the continuity pass below
      // without another projection round.
      const candidates = pts.map((pt) => {
        const perRoute = [];
        for (const set of sectionSets) {
          const hit = nearestOnSections(set.sections, pt);
          if (hit) perRoute.push({ key: set.key, hit, routeLength: set.routeLength });
        }
        return perRoute;
      });

      // Where the lane CROSSES another route, that route is momentarily the
      // nearest one and a few points would otherwise be labelled with its road
      // and its unrelated milepost. Points are ordered along the lane, so a real
      // road change is a long run and an intersection is a short one.
      const chosen = candidates.map((perRoute) => pickCandidate(perRoute));
      const smoothed = smoothRouteRuns(chosen.map((c) => c?.key ?? null));

      return candidates.map((perRoute, i) => {
        const want = smoothed[i];
        const reassigned = want != null && want !== (chosen[i]?.key ?? null)
          ? perRoute.find((c) => c.key === want)
          : null;
        const pick = reassigned ?? chosen[i];
        if (!pick) return null;
        const loc = locationFromHit(pick.hit, pick.routeLength);
        return {
          state: 'MA',
          road: loc.routeLabel,
          milepost: loc.postedMilepost ?? loc.measure,
          lrs_measure: loc.measure,
          county: loc.zone?.county ?? null,
          region: loc.zone?.region ?? null,
          // Municipality (city/town) name — only when the zone genuinely is
          // one (MassDOT `City` domain, 351 towns). `zone.name` falls back to
          // the county when the city code is absent, so gate on `code` to
          // never smuggle a county in as a town.
          town:
            loc.zone?.kind === 'municipality' && loc.zone?.code != null
              ? loc.zone.name
              : null,
          distance_m: Math.round(loc.distanceMeters * 10) / 10,
          speed_limit: loc.speedLimit ?? null,
          // Regulatory speed: the SSR regulation governing this measure when the
          // snapshot has one (tier precedence in resolveSsrSpeed), else the Road
          // Inventory value — the pre-SSR behaviour, kept as the live backup.
          // `speed_reg_source` + `reg_id`/`regulation_url` record which, so the
          // provenance flows into the *_POINTS_* collections and deliverables can
          // cite the actual signed regulation.
          ...(() => {
            const ssr = resolveSsrSpeed(ssrByKey.get(pick.key), loc.measure);
            if (ssr) {
              return {
                speed_limit_regulatory: ssr.speed,
                speed_reg_source: `ssr_${ssr.tier}`,
                reg_id: ssr.regId ?? null,
                regulation_url: ssr.url ?? null,
              };
            }
            return {
              speed_limit_regulatory: loc.speedRegulatory ?? null,
              speed_reg_source: 'road_inventory',
              reg_id: null,
              regulation_url: null,
            };
          })(),
        };
      });
    },
  };
}

// ─── Search WHERE-builder ─────────────────────────────────────────────────────

/** Builds the local-search WHERE clause. Recognises a route designation
 *  ("SR2", "I 93"), a municipality name, a bare street name, or a combination
 *  ("Main Street Cambridge"). Exported for testing. */
export function buildLocalWhere(term) {
  const cleaned = String(term ?? '').trim();
  if (!cleaned) return '1=1';

  const clauses = [];
  let working = cleaned;

  // Municipality first — many are multi-word ("North Adams"), so match the
  // longest name present before tokenizing whatever is left.
  const cityEntries = Object.entries(MA_CITY_NAMES)
    .map(([code, name]) => [code, name])
    .sort((a, b) => b[1].length - a[1].length);
  for (const [code, name] of cityEntries) {
    const re = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(working)) {
      clauses.push(`City = ${Number(code)}`);
      working = working.replace(re, ' ');
      break; // one municipality filter per search
    }
  }

  const rest = working.trim();
  if (rest) {
    // "SR2" / "I 93" / "US 20" → a route designation.
    const designation = rest.match(/^(I|US|SR)[\s-]?(\d+[A-Z]?)$/i);
    if (designation) {
      clauses.push(`Route_System = '${designation[1].toUpperCase()}'`);
      clauses.push(`Route_Number = '${sqlLiteral(designation[2].toUpperCase())}'`);
    } else {
      clauses.push(`UPPER(St_Name) LIKE '%${sqlLiteral(rest.toUpperCase())}%'`);
    }
  }

  return clauses.length ? clauses.join(' AND ') : '1=1';
}

export const massdotAdapter = createMassdotAdapter();
